import { Button } from "@/components/ui/button";
import { PageHeaderV2 } from "@/components/ui/page-header-v2";
import { StatusBadge } from "@/components/ui/status-badge";
import { familyOf } from "@/lib/family";
import * as Tabs from "@radix-ui/react-tabs";
import { useNavigate, useParams } from "@tanstack/react-router";
import { AnalyticsTab } from "./analytics-tab";
import { useModel } from "./queries";
import { RevisionsTable } from "./revisions-table";

export function ModelDetailPage() {
  const { modelId } = useParams({ from: "/models/$modelId" });
  const { data: m } = useModel(modelId);
  const navigate = useNavigate();

  const family = familyOf(m?.task_type);
  const activeRevNo = m?.active_revision?.revision_no ?? null;

  return (
    <>
      <PageHeaderV2
        eyebrow="Models"
        title={m?.name ?? "…"}
        meta={
          m ? (
            <div className="flex items-center gap-2">
              <StatusBadge status={m.status} />
              <span>{m.task_type}</span>
              <span>·</span>
              <span>
                {m.total_revisions} rev{m.total_revisions === 1 ? "" : "s"}
              </span>
            </div>
          ) : null
        }
        action={
          <Button
            variant="primary"
            onClick={() => navigate({ to: "/inference", search: { model: modelId } })}
          >
            Test inference →
          </Button>
        }
      />

      {m?.status === "draft" && (
        <div className="mt-4 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          No active revision — activate one to enable inference.
        </div>
      )}

      <Tabs.Root defaultValue="revisions" className="mt-6">
        <Tabs.List className="mb-6 flex gap-4 border-b border-line">
          <Tabs.Trigger
            value="revisions"
            className="border-b-2 border-transparent px-3 pb-2 pt-1 font-mono text-[11px] font-semibold uppercase tracking-wider text-muted transition-colors data-[state=active]:border-teal-500 data-[state=active]:text-ink"
          >
            Revisions
          </Tabs.Trigger>
          <Tabs.Trigger
            value="analytics"
            className="border-b-2 border-transparent px-3 pb-2 pt-1 font-mono text-[11px] font-semibold uppercase tracking-wider text-muted transition-colors data-[state=active]:border-teal-500 data-[state=active]:text-ink"
          >
            Analytics
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="revisions">
          <RevisionsTable modelId={modelId} />
        </Tabs.Content>

        <Tabs.Content value="analytics">
          {activeRevNo !== null ? (
            <AnalyticsTab modelId={modelId} revisionNo={activeRevNo} family={family} />
          ) : (
            <div className="rounded-[14px] border border-line bg-surface p-8 text-center">
              <p className="text-sm text-muted">No active revision — train a model first.</p>
            </div>
          )}
        </Tabs.Content>
      </Tabs.Root>
    </>
  );
}
